import { useEffect, useRef, useState } from 'react';
import './Tips.css';
import { AdvisoryContent, ViewElement } from '../../../common/global-types/AdvisoryContent';
import Tip from './Tip';
import Modal from '../modal/Modal';

export type ContentTipModalView = {
  contentId: string;
  tipViewId: number;
  modalViewId: number | undefined;
}

type TipsProps = {
  content: AdvisoryContent[];
  touchpointId: string;
  proxyPath: string | null;
};

function Tips({ content, touchpointId, proxyPath }: TipsProps) {
  const [activeItem, setActiveItem] = useState<number>(0);
  const [heading, setHeading] = useState<string>();
  const tipsList = useRef<HTMLUListElement | null>(null);

  const [contentTipModalViews, setContentTipModalViews] = useState<ContentTipModalView[]>(
    content.map((c) => ({
      contentId: c.id,
      tipViewId: c.initialView,
      modalViewId: undefined,
    }))
  );

  const initialViewElements = (contentItem: AdvisoryContent) => contentItem
    .contentViews![contentItem.initialView - 1].viewElements as ViewElement;

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const container = event.target as HTMLElement;
    const li = tipsList.current?.querySelector('li');

    if (li) {
      const itemWidth = li.getBoundingClientRect().width;
      const newActiveItem = Math.round(container.scrollLeft / itemWidth);
      setActiveItem(newActiveItem);
    }
  };

  useEffect(() => {
    switch (touchpointId) {
      case 'fi-fi-mypages-if':
        setHeading('Suosituksemme sinulle');
        break;
      case 'sv-fi-mypages-if':
        setHeading('Vår rekommendation till dig');
        break;
      case 'sv-se-mypages-if':
        setHeading('Tips till dig!');
        break;
      case 'nb-no-mypages-if':
        setHeading('Våre tips til deg');
        break;
      case 'da-dk-mypages-if':
        setHeading('Anbefalinger og tips til dig');
        break;
      default:
        setHeading('');
        break;
    }
  }, []);

  useEffect(() => {
    if (tipsList.current) {
      (tipsList.current as HTMLElement).style.setProperty('--advisory-tips-content-count', content.length.toString());
    }
  }, [content.length]);

  return (
    <div className="advisory-tip">
      <h2 className="if typography heading large">{heading}</h2>
      <ul ref={tipsList} className="advisory-tip-cards" onScroll={handleScroll}>
        {contentTipModalViews.map((item) => (
          <Tip
            key={item.contentId}
            content={content.find((c) => c.id === item.contentId)!}
            proxyPath={proxyPath}
            tipViewId={item.tipViewId}
            setContentTipModalViews={setContentTipModalViews}
          />
        ))}
      </ul>
      <nav className="if pagination advisory-tip-pagination" aria-label="Pagination Navigation">
        <ul className="if pagination-list dotted medium">
          {content.map((item, index) => (
            <li
              key={`${initialViewElements(item).headlines[0]}-index`}
              className="if pagination-item">
              <a
                className={`if pagination-link ${activeItem === index ? 'current' : ''}`}
                aria-label={`Go to page ${index + 1}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const list = tipsList.current;
                  const li = list?.querySelector('li');
                  if (list && li) {
                    const itemWidth = li.offsetWidth;
                    list.scrollTo({
                      left: index * itemWidth,
                      behavior: 'smooth',
                    });
                    setActiveItem(index);
                  }
                }}>
                <div className="if pagination-dot" aria-hidden="true"></div>
              </a>
            </li>
          ))}
        </ul>
      </nav>
      {contentTipModalViews.map((item) => (
        <Modal
          content={content.find((c) => c.id === item.contentId)!}
          viewId={item.modalViewId}
          setViewId={(viewId: number | undefined) => {
            let tipViewId: number | undefined = undefined;
            let modalViewId: number | undefined = undefined;
      
            const isModal = content.find((c) => c.id === item.contentId)?.contentViews?.find((cv) => cv.contentViewId === viewId)?.isModal;
      
            if (isModal) modalViewId = viewId;
            else tipViewId = viewId;
      
            setContentTipModalViews((prev: ContentTipModalView[]) => {
              return prev.map((view) =>
                item.contentId === view.contentId ? { ...view, tipViewId: tipViewId ?? view.tipViewId, modalViewId } : view
              );
            });
          }}
          proxyPath={proxyPath}
        />
      ))}
    </div>
  );
}

export default Tips;
